/**
 * WelcomeFrame — shared chrome for the 7-step Voice onboarding wizard.
 *
 * Full-bleed, pre-app, no sidebar: a thin brand wash, the "Help Genie Voice"
 * wordmark (Voice italic, gradient), "Step X of N" + step pips, an optional
 * mascot, eyebrow / title / subtitle, a centred content column, and a
 * Back / Continue footer with an optional footer note. Each step supplies only
 * its body via `children`.
 *
 * Brand law: signature gradient for wordmark + pips + primary CTA; genie tokens
 * only; "Help Genie" two words; *Voice* italic. No "AI" language; no em dashes.
 */
import type { ReactNode } from "react";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { brandAssets } from "@/lib/brand/tokens";
import type { VoiceMascotKey } from "@/types/voice-vision";

export interface OnboardStep {
  id: string;
  label: string;
}

interface WelcomeFrameProps {
  /** 1-based current step */
  step: number;
  steps: OnboardStep[];
  eyebrow?: string;
  title: string;
  subtitle?: string;
  showMascot?: boolean;
  mascot?: VoiceMascotKey;
  showBack?: boolean;
  showSkip?: boolean;
  backLabel?: string;
  continueLabel?: string;
  footerNote?: string;
  /**
   * Where the primary CTA lives. "footer" (default) pins Back / Continue to a
   * bottom bar; "inline" centres a single Continue button under the content and
   * drops the footer bar entirely (used by the welcome step).
   */
  ctaPlacement?: "footer" | "inline";
  /** Max width of the content column (px) */
  maxWidth?: number;
  onContinue?: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  /**
   * Per-step skip: renders a secondary "Skip for now" button beside Continue in
   * the footer. Distinct from `onSkip` (top-bar "Skip setup"), which abandons
   * the whole wizard — this one advances one step without its data.
   */
  onSkipStep?: () => void;
  skipStepLabel?: string;
  /** Disable the Continue CTA (e.g. invalid form) */
  continueDisabled?: boolean;
  children?: ReactNode;
}

export const WelcomeFrame = ({
  step,
  steps,
  eyebrow,
  title,
  subtitle,
  showMascot = true,
  mascot = "receptionist",
  showBack = true,
  showSkip = true,
  backLabel = "Back",
  continueLabel = "Continue",
  footerNote,
  ctaPlacement = "footer",
  maxWidth = 600,
  onContinue,
  onBack,
  onSkip,
  onSkipStep,
  skipStepLabel = "Skip for now",
  continueDisabled,
  children,
}: WelcomeFrameProps) => {
  return (
    <div className="flex flex-col h-screen w-full bg-genie-background text-[#222B45] overflow-hidden">
      {/* brand wash */}
      <div className="h-1 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />

      {/* top bar — wordmark + step progress + skip */}
      <div className="h-[60px] flex-shrink-0 px-6 md:px-10 flex items-center border-b border-[#F3F4F6]">
        <div className="flex items-center gap-2">
          <img
            src={brandAssets.icon}
            alt="Help Genie"
            className="w-7 h-7 object-contain"
          />
          <span className="text-[15px] font-bold text-genie-logo">Help Genie</span>
          <span className="text-[15px] font-extrabold italic bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
            Voice
          </span>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2.5">
          <span className="text-xs font-semibold text-gray-500 hidden sm:inline">
            Step {step} of {steps.length}
          </span>
          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => {
              const done = i < step - 1;
              const active = i === step - 1;
              return (
                <span
                  key={s.id}
                  className={`h-2 rounded-full transition-all ${
                    active
                      ? "w-[22px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                      : done
                        ? "w-2 bg-genie-primary"
                        : "w-2 bg-gray-200"
                  }`}
                />
              );
            })}
          </div>
          {showSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors ml-2"
            >
              Skip setup
            </button>
          )}
        </div>
      </div>

      {/* centred content column */}
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col items-center px-6 md:px-10 pt-12 pb-10">
          <div
            className="w-full flex flex-col items-center"
            style={{ maxWidth }}
          >
            {showMascot && (
              <div className="mb-4">
                <VoiceMascot mascot={mascot} size={80} rounded discSize={92} />
              </div>
            )}
            {eyebrow && (
              <p className="text-[11px] font-bold tracking-[1.6px] uppercase mb-2 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                {eyebrow}
              </p>
            )}
            <h1 className="text-[27px] font-extrabold text-[#222B45] tracking-[-0.5px] text-center leading-[1.18]">
              {title}
            </h1>
            {subtitle && (
              <p className="text-[15px] text-[#64748B] text-center leading-relaxed mt-4 max-w-[540px]">
                {subtitle}
              </p>
            )}
            {children && <div className="w-full mt-8">{children}</div>}
            {ctaPlacement === "inline" && (
              <button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled}
                className="mt-10 h-12 px-10 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 hover:opacity-90 transition-all disabled:opacity-50"
              >
                {continueLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* footer — Back / Continue */}
      {ctaPlacement === "footer" && (
        <div className="border-t border-[#F3F4F6] flex-shrink-0 py-4 px-6 md:px-10 flex flex-col items-center gap-2 bg-white">
          <div className="w-full flex items-center" style={{ maxWidth }}>
            {showBack ? (
              <button
                type="button"
                onClick={onBack}
                className="h-11 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-[#64748B] hover:bg-genie-background transition-colors"
              >
                {backLabel}
              </button>
            ) : (
              <div />
            )}
            <div className="flex-1" />
            {onSkipStep && (
              <button
                type="button"
                onClick={onSkipStep}
                className="h-11 px-5 mr-3 rounded-xl text-sm font-semibold text-[#64748B] hover:text-genie-primary hover:bg-genie-background transition-colors"
              >
                {skipStepLabel}
              </button>
            )}
            <button
              type="button"
              onClick={onContinue}
              disabled={continueDisabled}
              className="h-11 px-8 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 hover:opacity-90 transition-all disabled:opacity-50"
            >
              {continueLabel}
            </button>
          </div>
          {footerNote && <p className="text-xs text-gray-500 text-center">{footerNote}</p>}
        </div>
      )}
    </div>
  );
};

export default WelcomeFrame;
